import useFileSaver from '@hooks/useFileSaver';
import useRequest, { IDataRequest, IJustificativaRequest } from '@hooks/useRequest';
import IFatura, { FaturaTipo } from '@model/Fatura';
import { IFaturaItem } from '@model/FaturaItem';
import IFaturaStatus, { EnumFaturaStatus } from '@model/FaturaStatus';
import IFormaPagamento, { EnumFormaPagamento } from '@model/FormaPagamento';
import React from 'react';

export enum EnumModoFechamentoNegativo {
    None = 0,
    DeixarFaturaAberta = 1,
    FecharFaturaSubtrairSaldo = 2
}

export interface IRegistrarPagamentoRequest {
    valorRecebido: number
    formaPagamento: EnumFormaPagamento
    dataPagamento: Date | null
    cienteCreditoCliente: boolean
    modoFechamentoNegativo: EnumModoFechamentoNegativo
    notas: string
    imprimirRecibo: boolean | string
}

export interface IRegistrarPagamento extends IRegistrarPagamentoRequest {
    valorOriginal: number
    valor: number
    valorJuros: number
    valorMulta: number
    valorAtual: number
    valorPago: number
    abonarJurosMulta: string
    abonarJurosMultaDesabilitado: boolean
    saldo: number
    saldoAtual: number
    novoSaldo: number
}

export interface IFaturaFilter {
    faturaId?: number
    clienteNome?: string
    clienteCpf?: string
    clienteRemetenteWhatsapp?: string
    empresaId?: number
    competencia?: string
    faturaStatusId: EnumFaturaStatus
    tipo?: FaturaTipo
}

const controller = "Fatura";

export function useFaturaService () {
    const _request = useRequest();
    const fileSaver = useFileSaver();

    const listar = React.useCallback((request: IDataRequest<IFaturaFilter>) => {
        return _request.getData<IFatura[]>(`${controller}/Listar`, request);
    }, [_request]);

    const listarItens = React.useCallback((faturaId: number) => {
        return _request.get<IFaturaItem[]>(`${controller}/ListarItens/${faturaId}`);
    }, [_request]);

    const listarStatusAtivos = React.useCallback(() => {
        return _request.get<IFaturaStatus[]>(`${controller}/ListarStatusAtivos`);
    }, [_request]);

    const listarTipos = React.useCallback(() => {
        return _request.get<Record<FaturaTipo, string>>(`${controller}/ListarTipos`);
    }, [_request]);

    const listarFormasPagamentoAtivas = React.useCallback(() => {
        return _request.get<IFormaPagamento[]>(`${controller}/ListarFormasPagamentoAtivas`);
    }, [_request]);

    const listarFormasPagamentoManuaisAtivas = React.useCallback(() => {
        return _request.get<IFormaPagamento[]>(`${controller}/listarFormasPagamentoManuaisAtivas`);
    }, [_request]);

    const listarFaturasPorTermo = React.useCallback((termo: string) => {
        return _request.get<IFatura[]>(`${controller}/ListarPorTermo`, {
            params: {
                termo
            }
        })
    }, [_request])

    const registrarPagamento = React.useCallback((faturaId: number, request: IRegistrarPagamentoRequest) => {
        return _request.post<IFatura>(`${controller}/RegistrarPagamento/${faturaId}`, request);
    }, [_request]);

    const cancelar = React.useCallback((faturaId: number, request: IJustificativaRequest) => {
        return _request.post<IFatura>(`${controller}/Cancelar/${faturaId}`, request);
    }, [_request]);

    const reabrir = React.useCallback((faturaId: number, request: IJustificativaRequest) => {
        return _request.post<IFatura>(`${controller}/Reabrir/${faturaId}`, request);
    }, [_request]);

    const desfazerCancelamento = React.useCallback((faturaId: number, request: IJustificativaRequest) => {
        return _request.post<IFatura>(`${controller}/DesfazerCancelamento/${faturaId}`, request);
    }, [_request]);

    const baixarBoleto = React.useCallback((faturaId: number) => {
        return _request.download(`/${controller}/BaixarBoleto/${faturaId}`)
            .then(response => { 
                fileSaver.save(response, `fatura-${faturaId}.pdf`);
            });
    }, [_request, fileSaver]);

    const obterJurosMultaPorData = React.useCallback((faturaId: number, data: Date) => {
        return _request.get<IFatura>(`${controller}/ObterJurosMultaPorData/${faturaId}`, {
            params: {
                data
            }
        })
    }, [_request]);

    return React.useMemo(() => ({
        listar,
        listarItens, 
        listarStatusAtivos,
        listarTipos,
        listarFormasPagamentoAtivas, 
        listarFormasPagamentoManuaisAtivas, 
        listarFaturasPorTermo,
        registrarPagamento,
        cancelar,
        baixarBoleto,
        reabrir,
        desfazerCancelamento,
        obterJurosMultaPorData
    }), [listar, listarItens, listarStatusAtivos, listarTipos, listarFormasPagamentoAtivas, listarFormasPagamentoManuaisAtivas, listarFaturasPorTermo, registrarPagamento, cancelar, baixarBoleto, reabrir, desfazerCancelamento, obterJurosMultaPorData]);
}